/**
 * Filter bar component for the audit logs table.
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  Box,
  Select,
  DatePicker,
  Button,
  TextField,
  Stack,
  InlineStack,
  Icon,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone } from '@internationalized/date';
import {
  getActionOptions,
  getObjectTypeDisplayName,
  parseObjectTypeActionValue,
  getGroupedObjectTypeOptions,
  NO_OBJECT_ID_TYPES,
  type SelectOptionItem,
  type SelectOptionGroup,
} from '../../utils/object-type-mapping';
import { AUDIT_OBJECT_TYPE_TO_FIELDS } from '../../constants/audit-field-options';
import { formatAttributeName } from '../../utils/formatting';
import { getUserDisplayName } from '../../utils/user-formatting';
import {
  ALL_USERS_VALUE,
  ALL_OBJECT_TYPES_VALUE,
  getDefaultDateRange,
  buildFiltersFromValues,
  validateDateRangeForNonPremium,
  type FilterValue,
} from './filter-utils';
import { getAuditLogDayLimit } from '../../utils/access';
import type { AuditLogFilters } from '../../types';
import { isEmptyString, stringIncludes } from '@/utils/string';
import { useAuth } from '@/services/auth';
import { isAPITriggeredBroadcast, getCampaignTypeLabel } from '../../utils/campaign-mapping/campaign-types';

/** Estimated height (px) of each row in virtualized Select dropdowns */
const SELECT_OPTION_HEIGHT_PX = 36;

/** Attributes to exclude from the filter dropdown */
const EXCLUDED_FILTER_ATTRIBUTES = new Set(['google_analytics_client_id']);

interface AuditLogsFiltersProps {
  filterValues: FilterValue[];
  setFilterValues: React.Dispatch<React.SetStateAction<FilterValue[]>>;
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  onSetUiObjectType?: (uiObjectType: string | null) => void;
  uiObjectType?: string | null;
  context: 'account' | 'workspace';
  allUsers: Array<{ user_id: number; email: string; first_name?: string; last_name?: string; deleted?: boolean }>;
  isLoadingUsers?: boolean;
  onLoadUsers?: () => void;
  campaigns?: Array<{ id: number; name: string; type?: string | number }>;
  isLoadingCampaigns?: boolean;
  isFetchingMoreCampaigns?: boolean;
  hasMoreCampaigns?: boolean;
  onLoadMoreCampaigns?: () => void;
  onCampaignSearch?: (searchString: string) => void;
  campaignSearchString?: string;
  segments?: Array<{ id: number; name: string }>;
  isLoadingSegments?: boolean;
  onLoadSegments?: () => void;
  newsletters?: Array<{ id: number; name: string }>;
  isLoadingNewsletters?: boolean;
  transactionalMessages?: Array<{ id: number; name: string }>;
  isLoadingTransactionalMessages?: boolean;
  workspaces?: Array<{ id: number; name: string }>;
  roles?: Array<{ id: number; name: string }>;
}

export function AuditLogsFilters({
  filterValues,
  setFilterValues,
  onFiltersChange,
  onSetUiObjectType,
  uiObjectType,
  context,
  allUsers,
  isLoadingUsers = false,
  onLoadUsers,
  campaigns = [],
  isLoadingCampaigns = false,
  onCampaignSearch,
  campaignSearchString = '',
  segments = [],
  isLoadingSegments = false,
  onLoadSegments,
  newsletters = [],
  isLoadingNewsletters = false,
  transactionalMessages = [],
  isLoadingTransactionalMessages = false,
  workspaces = [],
  roles = [],
}: AuditLogsFiltersProps) {
  const auth = useAuth();
  const dayLimit = getAuditLogDayLimit(auth.account);
  const hasUnlimitedAccess = dayLimit == null;

  /**
   * Pluma Select pagination/search behavior can be sensitive to option identity.
   * The People filters keep dropdowns open by:
   * - Using the Select built-in `onLoadMore/shouldLoadMore/isLoadingMore`
   * - Slicing/filtering *without recreating option objects*
   *
   * We mirror that by caching option objects by a stable key and reusing them
   * across renders as new pages append.
   */
  const entityOptionCacheRef = useRef(new Map<string, { value: string; label: string }>());
  const campaignOptionCacheRef = useRef(new Map<string, { value: string; label: string }>());

  const getCachedOption = useCallback(
    (cache: Map<string, { value: string; label: string }>, cacheKey: string, value: string, label: string) => {
      const existing = cache.get(cacheKey);
      if (existing == null) {
        const created = { value, label };
        cache.set(cacheKey, created);
        return created;
      }
      // Keep identity stable, but allow labels to update if name changes
      if (existing.label !== label) {
        existing.label = label;
      }
      return existing;
    },
    [],
  );

  // Get current filter values
  const currentDateRangeRaw = filterValues.find((f) => f.key === 'date_range')?.value as
    | { start?: CalendarDate; end?: CalendarDate }
    | undefined;
  const currentDateRange =
    currentDateRangeRaw?.start != null && currentDateRangeRaw?.end != null
      ? { start: currentDateRangeRaw.start, end: currentDateRangeRaw.end }
      : null;

  // Calculate minimum allowed date for accounts with limited access
  // "Last N days" includes today, so min allowed = today - (N - 1)
  const minAllowedDate = useMemo(() => {
    if (hasUnlimitedAccess) {
      return undefined;
    }
    const endDate = today(getLocalTimeZone());
    return endDate.subtract({ days: dayLimit - 1 });
  }, [hasUnlimitedAccess, dayLimit]);

  // Presets for date picker - accounts with limited access only see presets within their day limit
  const datePickerPresets = useMemo(() => {
    if (hasUnlimitedAccess) {
      // Accounts with unlimited access get all default presets (DatePicker will use its defaults when presets is undefined)
      return undefined;
    }

    const presets: Array<{
      type: 'relative';
      label: string;
      timeAgo: { value: number; unit: 'days' };
    }> = [];

    // Standard presets - show all that are within the limit
    // "Last 24 hours" (1 day)
    if (dayLimit >= 1) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 24 hours',
        timeAgo: { value: 1, unit: 'days' as const },
      });
    }

    // "Last 7 days"
    if (dayLimit >= 7) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 7 days',
        timeAgo: { value: 7, unit: 'days' as const },
      });
    }

    // "Last 30 days"
    if (dayLimit >= 30) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 30 days',
        timeAgo: { value: 29, unit: 'days' as const },
      });
    }

    // Show custom limit preset only if it's not already one of the standard presets
    // Standard presets are: 1 day, 7 days, 30 days
    if (dayLimit !== 1 && dayLimit !== 7 && dayLimit !== 30) {
      presets.push({
        type: 'relative' as const,
        label: `Last ${dayLimit} days`,
        timeAgo: { value: dayLimit - 1, unit: 'days' as const },
      });
    }

    return presets;
  }, [hasUnlimitedAccess, dayLimit]);
  const currentUserId = (filterValues.find((f) => f.key === 'user_id')?.value as string) ?? ALL_USERS_VALUE;
  // Note: currentObjectTypeRaw may contain a combined value like "User:created" from URL params or row actions
  const currentObjectTypeRaw =
    (filterValues.find((f) => f.key === 'object_type')?.value as string) ?? ALL_OBJECT_TYPES_VALUE;
  // Extract just the object type from potentially combined format (e.g., "User:created" -> "User")
  const parsedObjectType = parseObjectTypeActionValue(currentObjectTypeRaw);
  const currentObjectType = parsedObjectType?.objectType ?? currentObjectTypeRaw;
  // Extract action from combined format or from separate action filter
  // When "Filter for related changes" is used, action is embedded in the combined format
  const currentAction =
    parsedObjectType?.action ?? (filterValues.find((f) => f.key === 'action')?.value as string) ?? '';
  const currentObjectId = (filterValues.find((f) => f.key === 'object_id')?.value as string) ?? '';
  const currentAttributeName = (filterValues.find((f) => f.key === 'attribute_name')?.value as string) ?? '';
  const currentIpAddress = (filterValues.find((f) => f.key === 'ip_addr')?.value as string) ?? '';
  const currentCampaignId = (filterValues.find((f) => f.key === 'campaign_id')?.value as string) ?? '';

  // Local state for text inputs
  const [objectIdInput, setObjectIdInput] = useState(currentObjectId);
  const [ipAddressInput, setIpAddressInput] = useState(currentIpAddress);

  // Sync local input state with filter values
  useEffect(() => {
    setObjectIdInput(currentObjectId);
  }, [currentObjectId]);

  useEffect(() => {
    setIpAddressInput(currentIpAddress);
  }, [currentIpAddress]);

  // Memoize default date range
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);

  // Object types that only have one action (created) - hide "Type of Change" dropdown
  const SINGLE_ACTION_OBJECT_TYPES = new Set(['JourneyExit', 'ProfileDeletion', 'ProfileMerge', 'ObjectDeletion']);

  // Object types with no meaningful attributes to filter - hide "Attribute Name" dropdown
  const NO_ATTRIBUTE_FILTER_OBJECT_TYPES = new Set(['JourneyExit', 'ProfileMerge', 'ObjectDeletion']);

  // Show filters only when a specific object type is selected (not "All Object Types")
  const hasSpecificObjectType = currentObjectType !== ALL_OBJECT_TYPES_VALUE;
  const showActionFilter = hasSpecificObjectType && !SINGLE_ACTION_OBJECT_TYPES.has(currentObjectType);
  const showAttributeFilter = hasSpecificObjectType && !NO_ATTRIBUTE_FILTER_OBJECT_TYPES.has(currentObjectType);
  const showObjectIdFilter = hasSpecificObjectType && !NO_OBJECT_ID_TYPES.has(currentObjectType);
  // Show campaign filter only for CampaignAction object type (workspace context only)
  const showCampaignFilter = context === 'workspace' && currentObjectType === 'CampaignAction';

  // Options for User Select dropdown - no icons on options, icon only on trigger
  const userSelectOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [{ value: ALL_USERS_VALUE, label: 'All team members' }];
    options.push({ value: '0', label: 'System' });

    for (const user of allUsers) {
      const name = getUserDisplayName(user);
      const label = name !== user.email ? `${name} (${user.email})` : user.email;
      options.push({
        value: user.user_id.toString(),
        label: user.deleted === true ? `${label} [Deleted]` : label,
      });
    }

    return options;
  }, [allUsers]);

  // Options for Object Type Select dropdown
  // Both contexts use grouped options with collapsible sections
  // The mixed array of flat option + groups is valid for Select's options prop
  // No icons on options, icon only on trigger
  const objectTypeSelectOptions = useMemo((): Array<SelectOptionItem | SelectOptionGroup> => {
    const groupedOptions = getGroupedObjectTypeOptions(context);
    const allTypesOption: SelectOptionItem = {
      value: ALL_OBJECT_TYPES_VALUE,
      label: 'All event types',
    };
    // Spread into array - Select component accepts both flat items and group items
    return [allTypesOption, ...groupedOptions];
  }, [context]);

  // Find the current object type option (needed for valueComponent when using grouped options)
  const currentObjectTypeOption = useMemo(() => {
    // Helper to search through flat options and grouped options
    const findOption = (
      options: Array<SelectOptionItem | SelectOptionGroup>,
      value: string,
    ): SelectOptionItem | null => {
      for (const option of options) {
        if ('options' in option) {
          // It's a group, recurse
          const found = findOption(option.options, value);
          if (found != null) return found;
        } else if (option.value === value) {
          // It's a flat option
          return option;
        }
      }
      return null;
    };
    const valueToFind = currentObjectType === ALL_OBJECT_TYPES_VALUE ? ALL_OBJECT_TYPES_VALUE : currentObjectType;
    return findOption(objectTypeSelectOptions, valueToFind);
  }, [objectTypeSelectOptions, currentObjectType]);

  // Get action options based on selected object type
  const actionSelectOptions = useMemo(() => {
    if (!showActionFilter) return [];
    return [{ value: '', label: 'All actions' }, ...getActionOptions(currentObjectType)];
  }, [showActionFilter, currentObjectType]);

  // Dynamic label for Object ID filter
  const objectIdFilterLabel = useMemo(() => {
    if (currentObjectType === ALL_OBJECT_TYPES_VALUE) return 'OBJECT ID';
    if (currentObjectType === 'WorkspacePermissions') {
      return 'WORKSPACE ID';
    }
    // Special case: JourneyExit should show "JOURNEY ID" not "END JOURNEY ID"
    if (currentObjectType === 'JourneyExit') {
      return 'JOURNEY ID';
    }
    // Special case: APITriggeredBroadcast should show "API TRIGGERED BROADCAST ID" (spaces, not hyphens)
    if (currentObjectType === 'APITriggeredBroadcast') {
      return 'API TRIGGERED BROADCAST ID';
    }
    // Special cases for deletion/merge events - use simpler labels
    if (currentObjectType === 'ProfileDeletion' || currentObjectType === 'ProfileMerge') {
      return 'PROFILE ID';
    }
    if (currentObjectType === 'ObjectDeletion') {
      return 'OBJECT ID';
    }
    const displayName = getObjectTypeDisplayName(currentObjectType, context);
    return `${displayName.toUpperCase()} ID`;
  }, [currentObjectType, context]);

  // Determine if we should show entity name dropdown instead of ID text field
  // For workspace context: Campaign, APITriggeredBroadcast, Segment, Newsletter, TransactionalMessage
  // For account context: Workspace, Role
  const showEntityNameDropdown =
    (context === 'workspace' &&
      (currentObjectType === 'Campaign' ||
        currentObjectType === 'APITriggeredBroadcast' ||
        currentObjectType === 'Segment' ||
        currentObjectType === 'Newsletter' ||
        currentObjectType === 'TransactionalMessage')) ||
    (context === 'account' && (currentObjectType === 'Workspace' || currentObjectType === 'Role'));

  // Get entity options for entity dropdown
  // For Campaign objects, group by campaign type (like CampaignAction dropdown)
  const entitySelectOptions = useMemo((): Array<SelectOptionItem | SelectOptionGroup> => {
    if (!showEntityNameDropdown) return [];

    // Use appropriate label for entity dropdown
    const entityLabel = currentObjectType === 'APITriggeredBroadcast' ? 'broadcast' : currentObjectType.toLowerCase();
    const cacheKeyPrefix = `${currentObjectType}:`;

    // Special handling for Campaign and APITriggeredBroadcast objects - group by campaign type
    if (currentObjectType === 'Campaign' || currentObjectType === 'APITriggeredBroadcast') {
      // Filter campaigns based on object type
      const filteredCampaigns = campaigns.filter((campaign) => {
        // For Campaign object type, exclude API-Triggered Broadcast campaigns
        if (currentObjectType === 'Campaign' && isAPITriggeredBroadcast(campaign.type)) {
          return false;
        }
        // For APITriggeredBroadcast object type, show only API-Triggered Broadcast campaigns
        if (currentObjectType === 'APITriggeredBroadcast' && !isAPITriggeredBroadcast(campaign.type)) {
          return false;
        }
        return true;
      });

      // Group campaigns by type
      const groupsByType = new Map<string, Array<(typeof campaigns)[0]>>();
      const ungroupedCampaigns: Array<(typeof campaigns)[0]> = [];

      for (const campaign of filteredCampaigns) {
        const campaignType = getCampaignTypeLabel(campaign.type);
        if (campaignType != null && campaignType.trim() !== '') {
          if (!groupsByType.has(campaignType)) {
            groupsByType.set(campaignType, []);
          }
          const group = groupsByType.get(campaignType);
          if (group != null) {
            group.push(campaign);
          }
        } else {
          ungroupedCampaigns.push(campaign);
        }
      }

      // Build grouped options
      const groupedOptions: Array<SelectOptionItem | SelectOptionGroup> = [];

      // Add "All" option first (use appropriate label based on object type)
      const allLabel = currentObjectType === 'APITriggeredBroadcast' ? 'All broadcasts' : `All ${entityLabel}s`;
      const allOption = getCachedOption(entityOptionCacheRef.current, `${cacheKeyPrefix}__all__`, '', allLabel);
      groupedOptions.push(allOption);

      // If we have groups, add them sorted by campaign type name
      if (groupsByType.size > 0) {
        const sortedGroupKeys = Array.from(groupsByType.keys()).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: 'accent' }),
        );

        for (const groupKey of sortedGroupKeys) {
          const groupCampaigns = groupsByType.get(groupKey);
          if (groupCampaigns == null) continue;

          // Sort campaigns within group by name
          groupCampaigns.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }));

          // Create options for this group
          const groupOptions: SelectOptionItem[] = groupCampaigns.map((campaign) => {
            const label = `${campaign.name} (#${campaign.id})`;
            return getCachedOption(
              entityOptionCacheRef.current,
              `${cacheKeyPrefix}${campaign.id}`,
              campaign.id.toString(),
              label,
            );
          });

          groupedOptions.push({
            label: groupKey,
            options: groupOptions,
          });
        }
      }

      // Add ungrouped campaigns if any
      if (ungroupedCampaigns.length > 0) {
        ungroupedCampaigns.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }));

        const ungroupedOptions: SelectOptionItem[] = ungroupedCampaigns.map((campaign) => {
          const label = `${campaign.name} (#${campaign.id})`;
          return getCachedOption(
            entityOptionCacheRef.current,
            `${cacheKeyPrefix}${campaign.id}`,
            campaign.id.toString(),
            label,
          );
        });

        // Always show ungrouped campaigns in a group for consistency
        const groupLabel = groupsByType.size > 0 ? 'Other campaigns' : 'Campaigns';
        groupedOptions.push({
          label: groupLabel,
          options: ungroupedOptions,
        });
      }

      return groupedOptions;
    }

    // For other entity types (Segment, Newsletter, etc.), use flat list
    let entities: Array<{ id: number; name: string }> = [];

    if (currentObjectType === 'Segment') {
      entities = segments;
    } else if (currentObjectType === 'Newsletter') {
      entities = newsletters;
    } else if (currentObjectType === 'TransactionalMessage') {
      entities = transactionalMessages;
    } else if (currentObjectType === 'Workspace') {
      entities = workspaces;
    } else if (currentObjectType === 'Role') {
      entities = roles;
    }

    const allOption = getCachedOption(
      entityOptionCacheRef.current,
      `${cacheKeyPrefix}__all__`,
      '',
      `All ${entityLabel}s`,
    );

    return [
      allOption,
      ...entities.map((entity) =>
        getCachedOption(
          entityOptionCacheRef.current,
          `${cacheKeyPrefix}${entity.id}`,
          entity.id.toString(),
          `${entity.name} (#${entity.id})`,
        ),
      ),
    ];
  }, [
    showEntityNameDropdown,
    currentObjectType,
    campaigns,
    segments,
    newsletters,
    transactionalMessages,
    workspaces,
    roles,
    getCachedOption,
  ]);

  const currentEntityOption = useMemo(() => {
    // Search through flat options and grouped options
    const findOption = (
      options: Array<SelectOptionItem | SelectOptionGroup>,
      value: string,
    ): SelectOptionItem | null => {
      for (const option of options) {
        if ('options' in option) {
          // It's a group, recurse
          const found: SelectOptionItem | null = findOption(option.options, value);
          if (found != null) return found;
        } else if (option.value === value) {
          // It's a flat option
          return option;
        }
      }
      return null;
    };
    const match = findOption(entitySelectOptions, currentObjectId);
    return match;
  }, [entitySelectOptions, currentObjectId]);

  // Get loading state for entity dropdown
  // Workspaces are loaded from auth context, so no loading state needed
  const isLoadingEntities = useMemo(() => {
    if (currentObjectType === 'Campaign' || currentObjectType === 'APITriggeredBroadcast') return isLoadingCampaigns;
    if (currentObjectType === 'Segment') return isLoadingSegments;
    if (currentObjectType === 'Newsletter') return isLoadingNewsletters;
    if (currentObjectType === 'TransactionalMessage') return isLoadingTransactionalMessages;
    // Workspaces are already loaded from auth context
    return false;
  }, [currentObjectType, isLoadingCampaigns, isLoadingSegments, isLoadingNewsletters, isLoadingTransactionalMessages]);

  // Handler to load entities when dropdown opens (for non-paginated entities)
  const handleEntityDropdownOpen = useCallback(() => {
    if (currentObjectType === 'Segment' && onLoadSegments != null) {
      onLoadSegments();
    }
  }, [currentObjectType, onLoadSegments]);

  // Get available attribute options based on selected object type
  const attributeSelectOptions = useMemo(() => {
    if (!showAttributeFilter) return [];

    let attributes: readonly string[] = [];
    if (!isEmptyString(currentObjectType)) {
      attributes = AUDIT_OBJECT_TYPE_TO_FIELDS[currentObjectType as keyof typeof AUDIT_OBJECT_TYPE_TO_FIELDS] ?? [];
    }

    // Filter out excluded attributes
    const filteredAttributes = attributes.filter((attr) => !EXCLUDED_FILTER_ATTRIBUTES.has(attr));

    return [
      { label: 'All attributes', value: 'all' },
      ...filteredAttributes.map((attr) => ({
        label: formatAttributeName(attr),
        value: attr,
      })),
    ];
  }, [showAttributeFilter, currentObjectType]);

  // Memoize campaign select options grouped by campaign type
  // Used for CampaignAction filter (show all campaigns regardless of type)
  const campaignSelectOptions = useMemo((): Array<SelectOptionItem | SelectOptionGroup> => {
    if (!showCampaignFilter) return [];

    // Helper function to build searchable text
    const getSearchText = (campaign: (typeof campaigns)[0]): string => {
      const campaignType = getCampaignTypeLabel(campaign.type);
      return campaignType != null ? `${campaign.name} ${campaignType}`.trim() : campaign.name;
    };

    // Filter campaigns based on search string
    const filteredCampaigns = campaigns.filter((campaign) => {
      if (campaignSearchString === '') return true;
      return stringIncludes(getSearchText(campaign), campaignSearchString);
    });

    // Group campaigns by type
    const groupsByType = new Map<string, Array<(typeof campaigns)[0]>>();
    const ungroupedCampaigns: Array<(typeof campaigns)[0]> = [];

    for (const campaign of filteredCampaigns) {
      const campaignType = getCampaignTypeLabel(campaign.type);
      if (campaignType != null && campaignType.trim() !== '') {
        if (!groupsByType.has(campaignType)) {
          groupsByType.set(campaignType, []);
        }
        const group = groupsByType.get(campaignType);
        if (group != null) {
          group.push(campaign);
        }
      } else {
        ungroupedCampaigns.push(campaign);
      }
    }

    // Build grouped options
    const groupedOptions: Array<SelectOptionItem | SelectOptionGroup> = [];

    // Add "All campaigns" option first
    const allOption = getCachedOption(campaignOptionCacheRef.current, '__all__', '', 'All campaigns');
    groupedOptions.push(allOption);

    // If we have groups, add them sorted by campaign type name
    if (groupsByType.size > 0) {
      const sortedGroupKeys = Array.from(groupsByType.keys()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'accent' }),
      );

      for (const groupKey of sortedGroupKeys) {
        const groupCampaigns = groupsByType.get(groupKey);
        if (groupCampaigns == null) continue;

        // Sort campaigns within group by name
        groupCampaigns.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }));

        // Create options for this group
        const groupOptions: SelectOptionItem[] = groupCampaigns.map((campaign) => {
          const label = `${campaign.name} (#${campaign.id})`;
          return getCachedOption(campaignOptionCacheRef.current, String(campaign.id), String(campaign.id), label);
        });

        groupedOptions.push({
          label: groupKey,
          options: groupOptions,
        });
      }
    }

    // Add ungrouped campaigns if any
    if (ungroupedCampaigns.length > 0) {
      ungroupedCampaigns.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }));

      const ungroupedOptions: SelectOptionItem[] = ungroupedCampaigns.map((campaign) => {
        const label = `${campaign.name} (#${campaign.id})`;
        return getCachedOption(campaignOptionCacheRef.current, String(campaign.id), String(campaign.id), label);
      });

      // Use "Other campaigns" if we have other groups, otherwise use "Campaigns"
      const groupLabel = groupsByType.size > 0 ? 'Other campaigns' : 'Campaigns';
      groupedOptions.push({
        label: groupLabel,
        options: ungroupedOptions,
      });
    }

    return groupedOptions;
  }, [showCampaignFilter, campaigns, campaignSearchString, getCachedOption]);

  const currentCampaignOption = useMemo(() => {
    // Search through flat options and grouped options
    for (const option of campaignSelectOptions) {
      if ('value' in option && option.value === currentCampaignId) {
        return option;
      }
      if ('options' in option) {
        // This is a group, search within its options
        const match = option.options.find((o) => o.value === currentCampaignId);
        if (match != null) {
          return match;
        }
      }
    }
    return null;
  }, [campaignSelectOptions, currentCampaignId]);

  // Filter handlers
  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      let newValues: FilterValue[] = [...filterValues];

      // DEPENDENCY LOGIC: When object_type changes, clear dependent filters
      // This prevents invalid filter combinations (e.g., Newsletter attributes with Campaign selected)
      if (key === 'object_type') {
        // Clear attribute_name: Different object types have different valid attributes
        // Clear action: Different object types have different valid actions (e.g., JourneyExit only has 'created')
        // Clear campaign_id: Only applicable for CampaignAction object type
        // Clear object_id: Object IDs are type-specific (e.g., Campaign #5 ≠ Newsletter #5)
        newValues = newValues.filter(
          (f) => f.key !== 'attribute_name' && f.key !== 'action' && f.key !== 'campaign_id' && f.key !== 'object_id',
        );
        // Clear the text input state when object_type changes
        setObjectIdInput('');
      }

      // DATE RANGE VALIDATION: For accounts with limited access, enforce day limit
      // This validation is also done in buildFiltersFromValues, but we do it here too
      // to ensure the UI state reflects the clamped values (including partial date ranges)
      if (key === 'date_range' && !hasUnlimitedAccess) {
        // Runtime type validation
        if (typeof value !== 'object' || value === null) {
          return; // Skip invalid date range
        }
        const dateRange = value as { start?: CalendarDate; end?: CalendarDate };
        // Use validateDateRangeForNonPremium to handle all cases:
        // - Both dates provided
        // - Only start date provided (clamps start, sets end to today)
        // - Only end date provided (clamps end, sets start appropriately)
        // - Both null (returns as-is)
        // This ensures UI state is always consistent with API filters
        const validatedRange = validateDateRangeForNonPremium(dateRange, dayLimit);
        value = validatedRange;
      }

      // Update or add the filter value
      const existingIndex = newValues.findIndex((f) => f.key === key);
      if (existingIndex !== -1) {
        // Filter already exists - update its value
        newValues[existingIndex] = { key, value };
      } else {
        // Filter doesn't exist - add it
        newValues = [...newValues, { key, value }];
      }

      // When object_type changes, update ui_object_type for client-side filtering
      let updatedUiObjectType = uiObjectType;
      if (key === 'object_type') {
        const objectTypeValue = typeof value === 'string' ? value : String(value);
        const parsed = parseObjectTypeActionValue(objectTypeValue);
        const newObjectType = parsed?.objectType ?? objectTypeValue;

        // Set uiObjectType for Campaign/APITriggeredBroadcast to enable client-side filtering
        // Clear for all other types (including "All event types")
        if (newObjectType === 'APITriggeredBroadcast' || newObjectType === 'Campaign') {
          updatedUiObjectType = newObjectType;
          onSetUiObjectType?.(newObjectType);
        } else {
          updatedUiObjectType = null;
          onSetUiObjectType?.(null);
        }
      }

      const newFilters = buildFiltersFromValues(newValues, dayLimit, updatedUiObjectType);

      // When object_type changes, explicitly clear dependent filters from URL
      // buildFiltersFromValues only sets values that exist, so we need to explicitly set undefined
      if (key === 'object_type') {
        newFilters.attribute_name = undefined;
        newFilters.action = undefined;
        newFilters.campaign_id = undefined;
        newFilters.object_id = undefined;
      }

      // TWO-STEP UPDATE: Update both local state and notify parent
      // 1. Local state: Keep UI in sync
      setFilterValues(newValues);
      // 2. Parent callback: Trigger API refetch with new filters
      onFiltersChange(newFilters);
    },
    [filterValues, setFilterValues, onFiltersChange, onSetUiObjectType, hasUnlimitedAccess, dayLimit, uiObjectType],
  );

  const handleFilterClear = useCallback(
    (key: string) => {
      if (key === 'date_range') {
        setFilterValues((prev) => {
          const newValues = prev.filter((f) => f.key !== key);
          newValues.push({ key: 'date_range', value: getDefaultDateRange() });
          return newValues;
        });
        const defaultRange = getDefaultDateRange();
        onFiltersChange({
          start_time: `${defaultRange.start.toString()}T00:00:00Z`,
          end_time: `${defaultRange.end.toString()}T23:59:59Z`,
          cursor: undefined,
        });
      } else if (key === 'user_id') {
        setFilterValues((prev) => {
          const newValues = prev.filter((f) => f.key !== key);
          newValues.push({ key: 'user_id', value: ALL_USERS_VALUE });
          return newValues;
        });
        onFiltersChange({ user_id: undefined, cursor: undefined });
      } else if (key === 'object_type') {
        setFilterValues((prev) => {
          const newValues = prev.filter(
            (f) =>
              f.key !== key &&
              f.key !== 'action' &&
              f.key !== 'attribute_name' &&
              f.key !== 'object_id' &&
              f.key !== 'campaign_id',
          );
          newValues.push({ key: 'object_type', value: ALL_OBJECT_TYPES_VALUE });
          return newValues;
        });
        setObjectIdInput('');
        onFiltersChange({
          object_type: undefined,
          action: undefined,
          attribute_name: undefined,
          object_id: undefined,
          campaign_id: undefined,
          cursor: undefined,
        });
      } else {
        setFilterValues((prev) => prev.filter((f) => f.key !== key));
        onFiltersChange({ [key]: undefined, cursor: undefined });
      }
    },
    [setFilterValues, onFiltersChange],
  );

  const handleFilterClearAll = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setFilterValues([
      { key: 'date_range', value: defaultRange },
      { key: 'user_id', value: ALL_USERS_VALUE },
      { key: 'object_type', value: ALL_OBJECT_TYPES_VALUE },
    ]);
    setObjectIdInput('');
    setIpAddressInput('');
    // Clear ui_object_type URL parameter to prevent stale Campaign/APITriggeredBroadcast filtering
    onSetUiObjectType?.(null);
    onFiltersChange({
      user_id: undefined,
      ip_addr: undefined,
      object_type: undefined,
      object_id: undefined,
      attribute_name: undefined,
      action: undefined,
      campaign_id: undefined,
      start_time: `${defaultRange.start.toString()}T00:00:00Z`,
      end_time: `${defaultRange.end.toString()}T23:59:59Z`,
      cursor: undefined,
    });
  }, [setFilterValues, onFiltersChange, onSetUiObjectType]);

  // Helper to create text field filter handlers
  const createTextFieldHandlers = useCallback(
    (filterKey: string, inputValue: string, currentValue: string, setInputValue: (value: string) => void) => {
      const applyFilter = () => {
        if (inputValue !== currentValue) {
          if (inputValue === '') {
            handleFilterClear(filterKey);
          } else {
            handleFilterChange(filterKey, inputValue);
          }
        }
      };

      return {
        onClear: () => {
          setInputValue('');
          handleFilterClear(filterKey);
        },
        onInput: (e: Event & { target: HTMLInputElement }) => {
          setInputValue(e.target.value);
        },
        onBlur: applyFilter,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            applyFilter();
          }
        },
      };
    },
    [handleFilterChange, handleFilterClear],
  );

  const objectIdHandlers = createTextFieldHandlers('object_id', objectIdInput, currentObjectId, setObjectIdInput);
  const ipAddressHandlers = createTextFieldHandlers('ip_addr', ipAddressInput, currentIpAddress, setIpAddressInput);

  // Check if any filters are applied (not default values)
  const hasActiveFilters =
    currentUserId !== ALL_USERS_VALUE ||
    currentObjectType !== ALL_OBJECT_TYPES_VALUE ||
    !isEmptyString(currentObjectId) ||
    !isEmptyString(currentAttributeName) ||
    !isEmptyString(currentIpAddress) ||
    (currentDateRange != null &&
      (currentDateRange.start.toString() !== defaultDateRange.start.toString() ||
        currentDateRange.end.toString() !== defaultDateRange.end.toString()));

  // Check if any nested filters should be shown
  const hasNestedFilters = showActionFilter || showAttributeFilter || showObjectIdFilter || showCampaignFilter;

  return (
    <Stack gap="200" mt="200">
      {/* Core filters row */}
      <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap="200">
        <Box style={{ minWidth: '280px' }}>
          <DatePicker
            label="DATE RANGE"
            isRange={true}
            withPresets={true}
            presets={datePickerPresets}
            value={currentDateRange}
            minDate={minAllowedDate}
            onChange={(value) => {
              handleFilterChange('date_range', value);
            }}
          />
        </Box>

        <Box style={{ minWidth: '300px' }}>
          <Select
            label="TEAM MEMBER"
            value={currentUserId}
            onChange={(value) => {
              if (value != null) {
                handleFilterChange('user_id', value);
              }
            }}
            options={userSelectOptions}
            placeholder="Select a user"
            isSearchable={true}
            onOpen={onLoadUsers}
            isLoading={isLoadingUsers}
            withVirtualizer={true}
            virtualizerOptions={{ estimateSize: () => SELECT_OPTION_HEIGHT_PX, overscan: 5 }}
            valueComponent={({ option }: { option?: SelectOptionItem }) => (
              <InlineStack alignBlock="center" gap="100">
                <Icon name="person" size="sm" />
                <span>{option?.label ?? ''}</span>
              </InlineStack>
            )}
          />
        </Box>

        <Box style={{ minWidth: '200px' }}>
          <TextField
            label="IP ADDRESS"
            value={ipAddressInput}
            placeholder="e.g., 192.168.1.1"
            icon="browser"
            isClearable={ipAddressInput !== ''}
            {...ipAddressHandlers}
          />
        </Box>

        <Box style={{ minWidth: '200px' }}>
          <Select
            label="EVENT TYPE"
            value={currentObjectType === ALL_OBJECT_TYPES_VALUE ? ALL_OBJECT_TYPES_VALUE : currentObjectType}
            onChange={(value) => {
              if (value != null) {
                // When user selects from dropdown, value can be either:
                // - A string (the option value)
                // - The full option object
                // Extract the string value if it's an object
                const stringValue = typeof value === 'string' ? value : (value as SelectOptionItem).value;
                if (stringValue != null) {
                  // When user selects from dropdown, value is just the object type (not combined)
                  // This clears any previously combined value from row actions
                  handleFilterChange('object_type', stringValue);
                }
              }
            }}
            options={objectTypeSelectOptions}
            placeholder="Select a type"
            isSearchable={true}
            valueComponent={({ option }: { option?: SelectOptionItem }) => {
              // If option is not provided, try to find it from currentObjectType
              const displayOption = option ?? currentObjectTypeOption;
              // Fallback to mapped display name if option not found
              const displayLabel =
                displayOption?.label ??
                (currentObjectType !== ALL_OBJECT_TYPES_VALUE
                  ? getObjectTypeDisplayName(currentObjectType, context)
                  : 'All event types');
              return (
                <InlineStack alignBlock="center" gap="100">
                  <Icon name="trigger-event" size="sm" />
                  <span>{displayLabel}</span>
                </InlineStack>
              );
            }}
          />
        </Box>

        {hasActiveFilters ? (
          <Button variant="tertiary" icon="close" onClick={handleFilterClearAll}>
            Reset all filters
          </Button>
        ) : null}
      </Box>

      {/* Nested filters row - shown when an event type is selected */}
      {hasNestedFilters ? (
        <Box
          display="inline-flex"
          alignItems="flex-end"
          flexWrap="wrap"
          gap="200"
          p="200"
          style={{
            backgroundColor: themeVars.palette['grey-100'],
            borderRadius: themeVars.radius['6'],
            width: 'fit-content',
          }}
        >
          {showObjectIdFilter && showEntityNameDropdown ? (
            <Box style={{ minWidth: '300px' }}>
              <Select
                key={`entity-select-${currentObjectType}`}
                label={
                  currentObjectType === 'APITriggeredBroadcast'
                    ? 'API TRIGGERED BROADCAST'
                    : getObjectTypeDisplayName(currentObjectType, context).toUpperCase()
                }
                value={currentEntityOption}
                onChange={(value) => {
                  const nextValue =
                    typeof value === 'string'
                      ? value
                      : value != null && typeof value === 'object' && 'value' in value
                      ? String((value as { value?: unknown }).value ?? '')
                      : null;

                  if (nextValue != null) {
                    if (nextValue === '') {
                      handleFilterClear('object_id');
                      setObjectIdInput('');
                    } else {
                      handleFilterChange('object_id', nextValue);
                      setObjectIdInput(nextValue);
                    }
                  }
                }}
                options={entitySelectOptions}
                placeholder={`Select ${currentObjectType.toLowerCase()}`}
                isSearchable={true}
                onOpen={handleEntityDropdownOpen}
                isLoading={isLoadingEntities}
                withVirtualizer={true}
                virtualizerOptions={{
                  estimateSize: () => SELECT_OPTION_HEIGHT_PX,
                  overscan: 5,
                }}
              />
            </Box>
          ) : showObjectIdFilter && currentObjectType !== 'Account' ? (
            <Box style={{ minWidth: '130px' }}>
              <TextField
                label={objectIdFilterLabel}
                value={objectIdInput}
                placeholder="Enter ID"
                isClearable={objectIdInput !== ''}
                {...objectIdHandlers}
              />
            </Box>
          ) : null}

          {showActionFilter ? (
            <Box style={{ minWidth: '150px' }}>
              <Select
                label="TYPE OF CHANGE"
                value={currentAction}
                onChange={(value) => {
                  if (value != null) {
                    if (value === '') {
                      handleFilterClear('action');
                    } else {
                      handleFilterChange('action', value);
                    }
                  }
                }}
                options={actionSelectOptions}
                placeholder="All actions"
                isSearchable={false}
              />
            </Box>
          ) : null}

          {showAttributeFilter ? (
            <Box style={{ minWidth: '180px' }}>
              <Select
                label="ATTRIBUTE NAME"
                value={isEmptyString(currentAttributeName) ? 'all' : currentAttributeName}
                onChange={(value) => {
                  if (value != null) {
                    if (value === 'all') {
                      handleFilterClear('attribute_name');
                    } else {
                      handleFilterChange('attribute_name', value);
                    }
                  }
                }}
                options={attributeSelectOptions}
                placeholder="Select attribute"
                isSearchable={true}
              />
            </Box>
          ) : null}

          {showCampaignFilter ? (
            <Box style={{ minWidth: '300px' }}>
              <Select
                key="campaign-select"
                label="CAMPAIGN"
                value={currentCampaignOption}
                onChange={(value) => {
                  const nextValue =
                    typeof value === 'string'
                      ? value
                      : value != null && typeof value === 'object' && 'value' in value
                      ? String((value as { value?: unknown }).value ?? '')
                      : null;

                  if (nextValue != null) {
                    if (nextValue === '') {
                      handleFilterClear('campaign_id');
                    } else {
                      handleFilterChange('campaign_id', nextValue);
                    }
                  }
                }}
                options={campaignSelectOptions}
                placeholder="Select campaign"
                isSearchable={true}
                searchInputValue={campaignSearchString}
                onSearchInput={(e: Event & { target: HTMLInputElement }) => {
                  onCampaignSearch?.(e.target.value);
                }}
                onCloseComplete={() => {
                  onCampaignSearch?.('');
                }}
                isLoading={isLoadingCampaigns}
                withVirtualizer={true}
                virtualizerOptions={{
                  estimateSize: () => SELECT_OPTION_HEIGHT_PX,
                  overscan: 5,
                }}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}
